import { ref, computed } from 'vue'
import { getApiBaseUrl, getWsBaseUrl } from '@/utils/apiBaseUrl'

// ===== ТИПЫ =====
export interface ChatEvent {
  type:
    | 'new_message'
    | 'new_chat'
    | 'chat_activity'
    | 'sync_needed'
    | 'message_status_update'
    | 'reactions_updated'
    | 'crm_outreach_run_updated'
    | 'crm_outreach_run_items_changed'
    | 'crm_ssot_updated'
    | 'drafts_updated'
    | 'llm_step_started'
    | 'llm_step_completed'
    | 'llm_step_failed'
    | 'task_updated'
    | 'task_progress'
  chat_id?: number
  internal_chat_id?: number
  telethon_account_id?: number
  tenant_id?: number
  message?: any
  data?: any
  [key: string]: any
}

export type ChatEventCallback = (event: ChatEvent) => void

// ===== ГЛОБАЛЬНОЕ СОСТОЯНИЕ =====
// Singleton WebSocket connection для всего приложения
let globalWs: WebSocket | null = null
let pingIntervalId: number | null = null
let reconnectTimeoutId: number | null = null
let isFirstConnection = true

const wsConnected = ref(false)
const eventBus = new Map<string, Set<ChatEventCallback>>()

// ===== ВСПОМОГАТЕЛЬНЫЕ ФУНКЦИИ =====
const getWsUrl = (): string => {
  const token = localStorage.getItem('token')
  if (!token) throw new Error('No token available')
  
  const wsUrl = getWsBaseUrl(getApiBaseUrl())
  return `${wsUrl}/messenger/ws?token=${encodeURIComponent(token)}`
}

const startPingInterval = () => {
  stopPingInterval()
  pingIntervalId = window.setInterval(() => {
    if (globalWs && globalWs.readyState === WebSocket.OPEN) {
      globalWs.send(JSON.stringify({ type: 'ping' }))
    }
  }, 30000)
  console.log('[MESSENGER_WS] Ping interval started')
}

const stopPingInterval = () => {
  if (pingIntervalId !== null) {
    window.clearInterval(pingIntervalId)
    pingIntervalId = null
  }
}

const stopReconnectTimeout = () => {
  if (reconnectTimeoutId !== null) {
    window.clearTimeout(reconnectTimeoutId)
    reconnectTimeoutId = null
  }
}

// ===== ОБРАБОТКА СОБЫТИЙ =====
const handleMessage = (event: MessageEvent) => {
  try {
    const data: ChatEvent = JSON.parse(event.data)
    console.log('[MESSENGER_WS] Received:', data.type, data)
    
    // Глобальные подписчики (все события)
    const globalCallbacks = eventBus.get('*')
    if (globalCallbacks) {
      globalCallbacks.forEach(cb => cb(data))
    }
    
    // Подписчики на конкретный тип события
    const typeCallbacks = eventBus.get(`type:${data.type}`)
    if (typeCallbacks) {
      typeCallbacks.forEach(cb => cb(data))
    }
    
    // Подписчики на конкретный чат
    // Support both legacy "flat" events (chat_id/internal_chat_id at top-level)
    // and ws_manager payloads that wrap fields inside `data`.
    const chatId =
      data.internal_chat_id ||
      data.chat_id ||
      (data.data && (data.data.internal_chat_id || data.data.chat_id))
    if (chatId) {
      const chatCallbacks = eventBus.get(`chat:${chatId}`)
      if (chatCallbacks) {
        chatCallbacks.forEach(cb => cb(data))
      }
    }
  } catch (e) {
    console.error('[MESSENGER_WS] Error parsing message:', e)
  }
}

// ===== ОСНОВНОЙ API =====
export function useMessengerWebSocket() {
  const connect = () => {
    if (globalWs && globalWs.readyState === WebSocket.OPEN) {
      console.log('[MESSENGER_WS] Already connected')
      return
    }
    
    stopReconnectTimeout()
    
    try {
      const url = getWsUrl()
      console.log('[MESSENGER_WS] Connecting...')
      
      globalWs = new WebSocket(url)
      
      globalWs.onopen = () => {
        console.log('[MESSENGER_WS] Connected')
        wsConnected.value = true
        
        if (isFirstConnection) {
          console.log('[MESSENGER_WS] First connection')
          isFirstConnection = false
        }
        
        startPingInterval()
      }
      
      globalWs.onmessage = handleMessage
      
      globalWs.onerror = (error) => {
        console.error('[MESSENGER_WS] Error:', error)
        wsConnected.value = false
      }
      
      globalWs.onclose = () => {
        console.log('[MESSENGER_WS] Disconnected')
        wsConnected.value = false
        stopPingInterval()
        
        // Auto-reconnect after 5 seconds
        reconnectTimeoutId = window.setTimeout(() => {
          console.log('[MESSENGER_WS] Reconnecting...')
          connect()
        }, 5000)
      }
    } catch (e) {
      console.error('[MESSENGER_WS] Error creating WebSocket:', e)
    }
  }
  
  const disconnect = () => {
    stopPingInterval()
    stopReconnectTimeout()
    
    if (globalWs) {
      globalWs.close()
      globalWs = null
    }
    wsConnected.value = false
  }
  
  // Подписка на события
  const subscribe = (
    filter: string | number,
    callback: ChatEventCallback
  ): (() => void) => {
    const key = typeof filter === 'number' 
      ? `chat:${filter}` 
      : filter
    
    if (!eventBus.has(key)) {
      eventBus.set(key, new Set())
    }
    eventBus.get(key)!.add(callback)
    
    console.log('[MESSENGER_WS] Subscribed to:', key, '(total:', eventBus.get(key)!.size, ')')
    
    // Возвращаем функцию отписки
    return () => {
      eventBus.get(key)?.delete(callback)
      console.log('[MESSENGER_WS] Unsubscribed from:', key)
    }
  }
  
  // Подписка на все события
  const subscribeToAll = (callback: ChatEventCallback): (() => void) => {
    return subscribe('*', callback)
  }
  
  // Подписка на тип события
  const subscribeToType = (type: string, callback: ChatEventCallback): (() => void) => {
    return subscribe(`type:${type}`, callback)
  }
  
  // Подписка на конкретный чат
  const subscribeToChat = (chatId: number, callback: ChatEventCallback): (() => void) => {
    return subscribe(chatId, callback)
  }
  
  // Отправка сообщения
  const send = (message: any) => {
    if (globalWs && globalWs.readyState === WebSocket.OPEN) {
      globalWs.send(JSON.stringify(message))
    } else {
      console.warn('[MESSENGER_WS] Cannot send: not connected')
    }
  }
  
  return {
    isConnected: computed(() => wsConnected.value),
    connect,
    disconnect,
    subscribe,
    subscribeToAll,
    subscribeToType,
    subscribeToChat,
    send
  }
}

